import {ProForm, ProFormCheckbox, ProFormText, ProFormTextArea} from '@ant-design/pro-form';
import {Typography} from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import {createLeadTaskRequest, editLeadTaskRequest} from '@/services/api/leadTask';
import {useDispatch, useIntl, useSelector} from 'umi';
import {LeadTaskModel} from '@/typings/models/LeadTask';
import {isEmpty} from 'lodash';
import styles from '@/components/Kanban/styles.less';
import ProFormDatePickerWithOptionalTime from '@/components/Form/ProFormDatePickerWithOptionalTime';
import LeadsSelect from '@/components/Filters/LeadsSelect';
import * as LeadSelectors from '@/selectors/leadSelectors';
import LeadAssigneeDropdownFilters from '@/components/Filters/LeadAssigneeDropdownFilters';
import {useHasAccess} from '@/utils/hooks';
import PERMISSIONS from '@/constants/permissions';
import {F} from '@/utils/intl';
import {LeadActionTag} from "@/components/ActionTag";
import {isLeadOwnedTask} from '@/utils/task';
import {processAPIResponseNow} from '@/utils/request';

type ICreateTaskFormProps = {
  leadId: string,
  fallbackLead?: any,
  setLoading: (loading: boolean) => void,
  updateRequest: () => void,
  onTaskCreated: () => void,
  form: any,
  task: LeadTaskModel
}
const CreateTaskForm = (({ leadId, fallbackLead, form, setLoading, updateRequest, onTaskCreated, task }: ICreateTaskFormProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const leadFromStore = useSelector((state: DefaultRootState) => LeadSelectors.selectLeadData(state, leadId));
  const preSelectedLead = leadFromStore || fallbackLead;
  const hasCreateTaskForOthersAccess = useHasAccess([PERMISSIONS.Tasks.CREATE_FOR_OTHERS, PERMISSIONS.BUSINESS_PARTNER]);

  const isEdit = !isEmpty(task);
  const leadOwned = isEdit && isLeadOwnedTask(task);

  const handleAfterSubmit = (response) => {
    setLoading(false);
    processAPIResponseNow(response, {
      onSuccess: () => {
        onTaskCreated();
        updateRequest
          ? updateRequest()
          : dispatch({
            type: 'leadTasks/fetch',
          });
      },
    });
  };

  const handleFinish = async (data) => {
    setLoading(true);
    if (leadId)
      data.leadId = leadId;
    // Normalize due_date to support both date-only and date+time selections
    const isDayjsValue = dayjs.isDayjs(data.due_date);
    const picked = isDayjsValue ? data.due_date : dayjs(data.due_date);
    const hasTime = picked.hour() !== 0 || picked.minute() !== 0 || picked.second() !== 0;

    if (isEdit) {
      data.due_date = hasTime
        ? picked.format('YYYY-MM-DD[T]HH:mm:ssZ')
        : picked.startOf('day').format('YYYY-MM-DD[T]00:00:00Z');
      // Owner changes on a lead-owned task are silently ignored by the API —
      // the field is hidden for those, and an empty array would 422 (min:1).
      if (leadOwned || isEmpty(data.assignee_ids)) delete data.assignee_ids;
      editLeadTaskRequest({ data, params: { taskId: task?.id } }).then(handleAfterSubmit);
    } else {
      const full = picked.format('YYYY-MM-DD[T]HH:mm:ssZ');
      const dateOnlyAtMidnight = picked.startOf('day').format('YYYY-MM-DD[T]00:00:00Z');
      data.due_date = hasTime ? full : dateOnlyAtMidnight;
      if (data.lead) {
        data.leadId = data.lead.id;
        delete data.lead;
      }
      createLeadTaskRequest({ data }).then(handleAfterSubmit);
    }
  };


  const getLeadFormItem = () => {
    const getContent = () => {
      if (preSelectedLead) return <div className={styles.preSelectedLead}>
        <LeadActionTag lead={preSelectedLead}/>
      </div>;


      return <LeadsSelect filterMode={'select'}
                          getFullObject
                          strongPlaceholder
                          showEmail
                          placeholder={<F id={'pages.tasks.noLead'} defaultMessage={'No Lead'}/>}
                          selectProps={{
                            width: '100%',
                          }}
      />;
    };

    return <ProForm.Item label={<F id={"entity.lead.singular"} defaultMessage={'Project'}/>} name={'lead'}>
      {getContent()}
    </ProForm.Item>;
  };
  return <ProForm onFinish={handleFinish} submitter={false} form={form}>
    {getLeadFormItem()}
    {hasCreateTaskForOthersAccess && leadOwned && (
      <ProForm.Item label={intl.formatMessage({
        id: 'entity.agent.singular',
        defaultMessage: 'Assignee',
      })}>
        <Typography.Text type="secondary">
          <F id={'pages.tasks.assignedToProjectAssignee'} defaultMessage={"Assigned to the project's assignee"} />
        </Typography.Text>
      </ProForm.Item>
    )}
    {hasCreateTaskForOthersAccess && !leadOwned && <ProForm.Item shouldUpdate style={{ margin: 0 }}>
      {({ getFieldValue }) => {
        const lead = getFieldValue('lead') || preSelectedLead;
        const placeholder = isEmpty(getFieldValue('assignee_ids')) && !lead ? <F id={'common.me'}
                                                                        defaultMessage={'Me'} />
          : <F id={'pages.tasks.defaultAssignedToLeadAssignee'}
               defaultMessage={"Lead's Assginee"} />;
        return <ProForm.Item
          name={'assignee_ids'}
          label={intl.formatMessage({
            id: 'entity.agent.plural',
            defaultMessage: 'Assignees',
          })
          }
          rules={isEdit ? [{
            required: true,
            message: intl.formatMessage({
              id: 'pages.tasks.assigneesRequired',
              defaultMessage: 'At least one assignee is required',
            }),
          }] : undefined}
        >
          <LeadAssigneeDropdownFilters
            filterMode={'select'}
            mode={'multiple'}
            placeholder={placeholder}
            strongPlaceholder
          />
        </ProForm.Item>;
      }}
    </ProForm.Item>}
    <ProFormText
      name="title"
      label={intl.formatMessage({
        id: 'common.attribute.title',
        defaultMessage: 'Title',
      })}
      rules={[{ required: true }]}
    />
    <ProFormTextArea
      name="description"
      label={intl.formatMessage({
        id: 'common.attribute.description',
        defaultMessage: 'Description',
      })}
      rules={[{ required: false }]}
    />
    <ProFormDatePickerWithOptionalTime
      name="due_date"
      label={intl.formatMessage({
        id: 'pages.lead.tasks.taskDue',
        defaultMessage: 'Due Date',
      })}
      rules={[{ required: true }]}
    />
    {task && !task.is_completed && (
      <ProForm.Item name="is_completed" valuePropName="checked">
        <ProFormCheckbox label={<F id="pages.lead.tasks.completed" defaultMessage="Completed" />} />
      </ProForm.Item>
    )}
  </ProForm>;
});

export default CreateTaskForm;
